/**
 * AWS Usage (ADJUSTMENT) scopes:
 * - Eligible: `SERVICE` scopes + optional `REGION` (include/exclude from UI).
 * - Ineligible: `INELIGIBLE_SERVICE` scopes (values from metadata `ineligible_services`) + optional `REGION`.
 * Ineligible service scopes always send `operator: INCLUDE`.
 *
 * Catalogs: GET .../metadata?kind=eligible_services | ineligible_services
 * Response: { kind, services: [{ display_name, name, service_codes, ... }] }
 */

/** Payload `scope_type` for AWS Usage ineligible services (eligible uses SERVICE). */
export const BILLING_SCOPE_TYPE_INELIGIBLE_SERVICE = "INELIGIBLE_SERVICE";

/** UI / create payload helper: eligible adjustment row in a rule block. */
export const ADJUSTMENT_PARTITION_ELIGIBLE = "ELIGIBLE";

/** UI / create payload helper: ineligible adjustment row (from ineligible_services metadata). */
export const ADJUSTMENT_PARTITION_INELIGIBLE = "INELIGIBLE";

const LEGACY_INELIGIBLE_ADJUSTMENT_TYPES = new Set(["INELIGIBLE_MARGIN", "INELIGIBLE_DISCOUNT"]);

export function isDeprecatedAdjustmentRuleType(type) {
  return LEGACY_INELIGIBLE_ADJUSTMENT_TYPES.has(type);
}

/** Map legacy API-only types to MARGIN/DISCOUNT + partition. */
export function mapLegacyAdjustmentTypeToTypeAndPartition(ruleType) {
  if (ruleType === "INELIGIBLE_MARGIN") {
    return { type: "MARGIN", partition: ADJUSTMENT_PARTITION_INELIGIBLE };
  }
  if (ruleType === "INELIGIBLE_DISCOUNT") {
    return { type: "DISCOUNT", partition: ADJUSTMENT_PARTITION_INELIGIBLE };
  }
  return { type: ruleType, partition: ADJUSTMENT_PARTITION_ELIGIBLE };
}

/**
 * Create/update API `rule_type`: UI keeps MARGIN/DISCOUNT + adjustmentPartition;
 * ineligible partition must send INELIGIBLE_MARGIN / INELIGIBLE_DISCOUNT.
 * @param {string} uiType - MARGIN, DISCOUNT, or legacy INELIGIBLE_* (normalized internally)
 * @param {string} [adjustmentPartition]
 */
export function mapAdjustmentTypeAndPartitionToApiRuleType(uiType, adjustmentPartition) {
  const { type: base } = mapLegacyAdjustmentTypeToTypeAndPartition(uiType);
  if (adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE) {
    if (base === "DISCOUNT") return "INELIGIBLE_DISCOUNT";
    if (base === "MARGIN") return "INELIGIBLE_MARGIN";
  }
  return base;
}

/** Normalize selectedRules when loading persisted state or legacy data. */
export function migrateSelectedRulesAdjustmentPartitions(selectedRules) {
  return (selectedRules || []).map((r) => {
    if (r.category !== "ADJUSTMENT") return r;
    if (LEGACY_INELIGIBLE_ADJUSTMENT_TYPES.has(r.type)) {
      const { type, partition } = mapLegacyAdjustmentTypeToTypeAndPartition(r.type);
      return { ...r, type, adjustmentPartition: partition };
    }
    if (r.adjustmentPartition) return r;
    return { ...r, adjustmentPartition: ADJUSTMENT_PARTITION_ELIGIBLE };
  });
}

/**
 * Normalize customer metadata eligible_services / ineligible_services (and legacy flat lists).
 * Prefer services[].display_name for the Service Add picker; fall back to name.
 * Only include services with mapped === true.
 */
export function normalizeMetadataCodeList(responseData) {
  if (!responseData || typeof responseData !== "object") return [];

  if (Array.isArray(responseData.services)) {
    const seen = new Set();
    const out = [];
    responseData.services.forEach((item) => {
      if (!item || typeof item !== "object" || item.mapped !== true) return;
      const label = String(item.display_name ?? item.name ?? "").trim();
      if (!label || seen.has(label)) return;
      seen.add(label);
      out.push(label);
    });
    return out;
  }

  const list =
    responseData.values ??
    responseData.service_codes ??
    responseData.ineligible_service_codes ??
    [];
  return Array.isArray(list) ? list : [];
}

/**
 * Eligible catalog is now its own metadata kind; keep helper for call-site compatibility.
 * @param {unknown} eligibleServices - labels from `eligible_services` metadata
 * @param {Set<string>} [_ineligibleSet] - unused (catalogs are separate APIs)
 */
export function getEligibleServiceCodesFromApi(eligibleServices, _ineligibleSet) {
  if (!Array.isArray(eligibleServices)) return [];
  return eligibleServices;
}

/**
 * @param {unknown} selectedServices
 * @param {Set<string>} ineligibleSet
 */
export function partitionSelectedServicesForAwsUsage(selectedServices, ineligibleSet) {
  const set = ineligibleSet instanceof Set ? ineligibleSet : new Set();
  if (!Array.isArray(selectedServices)) return { eligible: [], ineligible: [] };
  const eligible = [];
  const ineligible = [];
  for (const s of selectedServices) {
    if (set.has(s)) ineligible.push(s);
    else eligible.push(s);
  }
  return { eligible, ineligible };
}
